type TemplateHtmlCache = Record<string, string>;

type GetCachedTemplateHtmlParams = {
  templateHtmlById: TemplateHtmlCache;
  templateId?: string;
  versionValue?: string;
};

type FetchTemplateHtmlParams = {
  presignedUrl: string;
  templateHtmlById: TemplateHtmlCache;
  templateId?: string;
  versionValue?: string;
  signal?: AbortSignal;
  onCacheWrite?: (cacheKey: string, html: string) => void;
};

export function getTemplateIdVersionKey(
  templateId?: string,
  versionValue?: string,
): string {
  if (!templateId) return "";
  return `${templateId}:${versionValue || "Draft"}`;
}

export function getCachedTemplateHtml({
  templateHtmlById,
  templateId,
  versionValue,
}: GetCachedTemplateHtmlParams): { cacheKey: string; html: string } {
  const cacheKey = getTemplateIdVersionKey(templateId, versionValue);
  return {
    cacheKey,
    html: cacheKey ? templateHtmlById[cacheKey] || "" : "",
  };
}

export async function getTemplateHtml({
  presignedUrl,
  templateHtmlById,
  templateId,
  versionValue,
  signal,
  onCacheWrite,
}: FetchTemplateHtmlParams): Promise<{
  cacheKey: string;
  html: string;
  fromCache: boolean;
}> {
  const { cacheKey, html: cachedHtml } = getCachedTemplateHtml({
    templateHtmlById,
    templateId,
    versionValue,
  });

  if (cachedHtml.trim()) {
    return { cacheKey, html: cachedHtml, fromCache: true };
  }

  const response = await fetch(presignedUrl, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch template: ${response.status}`);
  }

  const html = await response.text();
  if (cacheKey && onCacheWrite) {
    onCacheWrite(cacheKey, html);
  }

  return { cacheKey, html, fromCache: false };
}
